import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { DashList, Formula, LearnArticleLink, LearnSeriesNav, TipiLink } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const k = (field: string) => `learn.articles.que-es-el-prime-cost.${field}`

const DOL_TIPPED_HREF = 'https://www.dol.gov/agencies/whd/state/minimum-wage/tipped'
const BOE_COTIZACION_HREF = 'https://www.boe.es/boe/dias/2026/03/31/pdfs/BOE-A-2026-7296.pdf'
const FUNDAE_HREF =
    'https://www.fundae.es/docs/default-source/publicaciones-y-evaluaciones/publicaciones-sectoriales/tendencias/estudio-tendencias-hosteler%C3%ADa.pdf?sfvrsn=cc1f74a_1'
const CEHAT_HREF =
    'https://cehat.com/wp-content/uploads/2025/05/29052025-La-restauracion-y-el-alojamiento-consideran-la-reduccion-de-la-jornada-laboral-una-perdida-de-competitividad-para-el-sector.pdf'
const INE_HREF =
    'https://www.ine.es/dyngs/INEbase/operacion.htm?c=Estadistica_C&cid=1254736176865&menu=ultiDatos&idp=1254735576550'

const SOURCES: { href?: string; labelKey: string; fallback: string }[] = [
    {
        href: CEHAT_HREF,
        labelKey: 'sources.1',
        fallback: 'Hostelería de España y CEHAT. Comunicado sobre la reducción de jornada, 29 de mayo de 2025.',
    },
    {
        href: FUNDAE_HREF,
        labelKey: 'sources.2',
        fallback:
            'Fundae (elaborado por PwC). Estudio de evolución y tendencias del sector de hostelería, abril de 2025, a partir de la Estadística Estructural de Empresas y la EPA del INE.',
    },
    {
        href: INE_HREF,
        labelKey: 'sources.3',
        fallback: 'INE. Estadística Estructural de Empresas: Sector Servicios.',
    },
    {
        href: BOE_COTIZACION_HREF,
        labelKey: 'sources.4',
        fallback: 'BOE. Orden PJC/297/2026, de 30 de marzo, de cotización a la Seguridad Social para 2026.',
    },
    {
        href: DOL_TIPPED_HREF,
        labelKey: 'sources.5',
        fallback: 'U.S. Department of Labor, Wage and Hour Division. Minimum wages for tipped employees.',
    },
    {
        labelKey: 'sources.6',
        fallback:
            'Benchmarks de prime cost de la industria estadounidense (RestaurantOwner, Mirus, 7shifts, Baker Tilly), consultados en octubre de 2026.',
    },
    {
        labelKey: 'sources.7',
        fallback:
            'Skello y ComboHR, rangos de coste de personal en restauración española, consultados en octubre de 2026.',
    },
]

export function QueEsElPrimeCostArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'que-es', label: t(k('toc.1'), '1. Qué es el prime cost') },
        { id: '70-por-ciento', label: t(k('toc.2'), '2. ¿El 70 % te deja a salvo?') },
        { id: 'tres-razones', label: t(k('toc.3'), '3. Por qué tu 70 % no es el de los manuales') },
        { id: 'alquiler', label: t(k('toc.4'), '4. Tu barrera depende del alquiler') },
        { id: 'sin-nomina', label: t(k('toc.5'), '5. Si curras sin nómina, el número es falso') },
        { id: 'antes-domingo', label: t(k('toc.6'), '6. Tres números antes del domingo') },
        { id: 'limites', label: t(k('toc.7'), '7. Lo que nadie ha medido todavía') },
    ]

    const sundayItems = [
        {
            lead: t(k('list.domingo.1Strong'), 'Producto consumido, no comprado.'),
            rest: t(
                k('list.domingo.1After'),
                ' Compras más existencias iniciales menos finales. Con las facturas solas, el mes que llenas la cámara parece un desastre.',
            ),
        },
        {
            lead: t(k('list.domingo.2Strong'), 'Personal a coste empresa.'),
            rest: t(
                k('list.domingo.2After'),
                ' Pide a la gestoría el coste total de Seguridad Social del mes, no las nóminas. Lo pagado en mano también es personal.',
            ),
        },
        {
            lead: t(k('list.domingo.3Strong'), 'Tu sueldo y tu techo.'),
            rest: t(
                k('list.domingo.3After'),
                ' Ponte el coste de quien te sustituiría según el convenio de tu provincia, y calcula tu máximo con la fórmula del alquiler.',
            ),
        },
    ]

    const limitItems = [
        t(
            k('list.limites.1'),
            'No existe un benchmark español oficial de prime cost. El 65 % y el 70 % vienen de la industria estadounidense.',
        ),
        t(
            k('list.limites.2'),
            'Las referencias en español no coinciden: unas ponen el personal sano en el 25–30 % y otras en el 35–40 %. Con un food cost habitual, el prime cost «normal» iría del 55 % al 72 %.',
        ),
        t(
            k('list.limites.3'),
            'Cuántos puntos de ventaja da la propina estadounidense no lo ha calculado nadie que hayamos encontrado.',
        ),
        t(k('list.limites.4'), 'Los datos del INE son de 2022 y mezclan bares, independientes y cadenas.'),
        t(
            k('list.limites.5'),
            'Qué sueldo ponerte no tiene regla. El coste de sustituirte según convenio es lo más defendible, pero es una convención.',
        ),
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'El prime cost es lo que gastas en producto más lo que gastas en personal, dividido entre lo que facturas sin IVA. La referencia del sector pone la alarma en el 65 %, y el 70 % es donde casi nadie gana dinero. En España, el personal ya se lleva de media el 31 % de la facturación, y la restauración cerró 2022 con un excedente bruto del 8,8 %. Si curras cada día sin nómina, tu prime cost real es más alto que el que te sale.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(k('heroAlt'), 'Cocina de restaurante con nóminas y albaranes sobre la mesa de pase')}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGapMd, 'text-article-muted-soft')}>
                                {t(k('published'), 'Publicado el 27 de octubre de 2026.')}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(k('intro.1'), 'Te llama la gestoría. «El food cost te sale al 29 %, vas bien.»')}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(
                                    k('intro.2'),
                                    'Cuelgas tranquilo. Y a final de mes, otra vez, la cuenta no da para lo que trabajas.',
                                )}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                                {t(k('intro.3'), 'No te han mentido. Te han dado la mitad del número.')}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='que-es' className={articleUi.h2}>
                        {t(k('h2.queEs'), 'Qué es el prime cost de un restaurante')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(
                                    k('p.queEs.1Strong'),
                                    'El prime cost es la suma del coste de producto consumido y del coste total de personal, expresada como porcentaje de las ventas sin IVA.',
                                )}
                            </strong>
                        </p>
                        <Formula>
                            {t(
                                k('formula.primeCost'),
                                'Prime cost % = (producto consumido + personal a coste empresa) ÷ ventas sin IVA × 100',
                            )}
                        </Formula>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.queEs.2'),
                                'Se miran juntos porque se compensan. Si compras elaborado, sube el food cost y baja la cocina.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.queEs.3Before'), 'Por eso el food cost solo engaña. ')}
                            <strong>
                                {t(
                                    k('p.queEs.3Strong'),
                                    'Un food cost del 29 % con personal al 42 % da un prime cost del 71 %. Uno del 34 % con personal al 28 % da un 62 %.',
                                )}
                            </strong>
                        </p>
                    </div>

                    <div className={articleUi.compare}>
                        <div className={articleUi.compareCard}>
                            <p className={articleUi.compareLabelAlert}>{t(k('compare.a.label'), 'Local A')}</p>
                            <p className={articleUi.compareTitle}>
                                {t(k('compare.a.title'), 'Food cost «bajo», prime cost 71 %')}
                            </p>
                            <p className={articleUi.compareBody}>
                                {t(k('compare.a.body'), 'Producto 29 % · Personal 42 % · Casi nadie gana dinero')}
                            </p>
                        </div>
                        <div className={articleUi.compareCard}>
                            <p className={articleUi.compareLabel}>{t(k('compare.b.label'), 'Local B')}</p>
                            <p className={articleUi.compareTitle}>
                                {t(k('compare.b.title'), 'Food cost «alto», prime cost 62 %')}
                            </p>
                            <p className={articleUi.compareBody}>
                                {t(k('compare.b.body'), 'Producto 34 % · Personal 28 % · El que gana dinero')}
                            </p>
                        </div>
                    </div>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.queEs.4Before'),
                                'El segundo local, el del «food cost alto», es el que gana dinero. La primera mitad de la suma la desmontamos en ',
                            )}
                            <LearnArticleLink slug='que-es-el-food-cost'>
                                {t(k('p.queEs.4Link'), 'por qué el food cost no es un solo número')}
                            </LearnArticleLink>
                            {t(k('p.queEs.4After'), '.')}
                        </p>
                    </div>

                    <h2 id='70-por-ciento' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.borde'), '¿Por debajo del 70 % de prime cost estás a salvo?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.borde.1Before'), 'El 70 % circula desde hace años y algo de verdad tiene. ')}
                            <strong>
                                {t(
                                    k('p.borde.1Strong'),
                                    'Por encima del 70 %, salvo volúmenes de venta muy altos, casi ningún restaurante gana dinero.',
                                )}
                            </strong>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.borde.2Before'),
                                'Lo que no se sostiene es leerlo al revés. La referencia más citada en la industria estadounidense dice que un restaurante con servicio de mesa no debería pasar del ',
                            )}
                            <strong>{t(k('p.borde.2Strong'), '65 %')}</strong>
                            {t(k('p.borde.2After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.borde.3'), 'Ahí empiezan los problemas. En el 70 % ya son casi seguros.')}
                        </p>
                        <blockquote className={articleUi.quoteSm}>
                            {t(
                                k('quote.borde'),
                                'El 70 % es el borde, no la meta. La línea de alarma está en el 65 %.',
                            )}
                        </blockquote>
                    </div>

                    <h2 id='tres-razones' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.razones'), 'Tres razones por las que tu 70 % no es el de los manuales')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(
                                    k('p.razones.1Strong'),
                                    'La primera: el número viene de un país donde la propina paga sueldos.',
                                )}
                            </strong>{' '}
                            {t(k('p.razones.1Before'), 'En Estados Unidos, la ley federal permite pagar al camarero ')}
                            <a
                                href={DOL_TIPPED_HREF}
                                target='_blank'
                                rel='noopener noreferrer'
                                className={articleUi.link}
                            >
                                {t(k('p.razones.1Link'), '2,13 dólares la hora')}
                            </a>{' '}
                            {t(k('p.razones.1After'), 'si las propinas completan el salario mínimo.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.razones.1b'),
                                'Parte de la sala la paga el cliente fuera del ticket. Aquí no, así que a igualdad de local lo lógico es que el personal pese más en España.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(
                                    k('p.razones.2Strong'),
                                    'La segunda: la nómina bruta no es lo que te cuesta un trabajador.',
                                )}
                            </strong>{' '}
                            {t(k('p.razones.2Before'), 'La ')}
                            <a
                                href={BOE_COTIZACION_HREF}
                                target='_blank'
                                rel='noopener noreferrer'
                                className={articleUi.link}
                            >
                                {t(k('p.razones.2Link'), 'Orden PJC/297/2026')}
                            </a>{' '}
                            {t(
                                k('p.razones.2After'),
                                'fija para la empresa un 23,60 % de contingencias comunes, un 5,50 % de desempleo indefinido, un 0,60 % de formación, un 0,20 % de FOGASA y un 0,75 % de MEI.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(
                                    k('p.razones.2bStrong'),
                                    'Eso es un 30,65 % encima del bruto, sin contar accidentes de trabajo.',
                                )}
                            </strong>{' '}
                            {t(
                                k('p.razones.2bAfter'),
                                'Si sumas nóminas brutas, te falta casi un tercio del gasto de personal.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>{t(k('p.razones.3Strong'), 'La tercera: el IVA en el denominador.')}</strong>{' '}
                            {t(k('p.razones.3After'), 'Si facturas 30.000 € sin IVA, cobras 33.000 € con el 10 %.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.razones.3b'),
                                'Un prime cost del 60 % sale al 54,5 % si divides por lo cobrado. Cinco puntos y medio de tranquilidad falsa.',
                            )}
                        </p>
                    </div>

                    <h2 id='alquiler' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.alquiler'), 'Tu barrera depende de lo que pagas de alquiler')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.alquiler.1'),
                                'Lo que no se lleva el prime cost paga alquiler, suministros, gestoría, seguros, datáfono, mantenimiento e impuestos. Lo que sobra es beneficio.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(
                                    k('p.alquiler.2Strong'),
                                    'Según la Estadística Estructural de Empresas del INE, la restauración española facturó 61.074 millones de euros en 2022 y tuvo un excedente bruto de explotación de 5.373 millones',
                                )}
                            </strong>
                            {t(k('p.alquiler.2Mid'), ', recogido en el ')}
                            <a href={FUNDAE_HREF} target='_blank' rel='noopener noreferrer' className={articleUi.link}>
                                {t(k('p.alquiler.2Link'), 'estudio sectorial de Fundae')}
                            </a>
                            {t(k('p.alquiler.2After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(
                                    k('p.alquiler.3Strong'),
                                    'Eso es un 8,8 % sobre la facturación, antes de amortizar e impuestos.',
                                )}
                            </strong>{' '}
                            {t(
                                k('p.alquiler.3After'),
                                'Y ese 8,8 % incluye lo que se llevan los dueños autónomos, que no cobran nómina.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.alquiler.4Before'),
                                'Mientras, el personal ya pesa lo suyo: según Hostelería de España, ',
                            )}
                            <a href={CEHAT_HREF} target='_blank' rel='noopener noreferrer' className={articleUi.link}>
                                {t(k('p.alquiler.4Link'), 'el salario supone de media el 31 % de la facturación anual')}
                            </a>
                            {t(k('p.alquiler.4After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.alquiler.5'), 'Tu techo de prime cost se calcula así:')}
                        </p>
                        <Formula>
                            {t(
                                k('formula.techo'),
                                'Prime cost máximo = 100 − resto de gastos (%) − beneficio que quieres (%)',
                            )}
                        </Formula>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.alquiler.6Before'),
                                'Un local de 30.000 € al mes con 3.000 € de alquiler, 1.800 € de suministros y 1.500 € de otros gastos tiene un 21 % de resto. Para ganar un 8 %, su techo está en el ',
                            )}
                            <strong>{t(k('p.alquiler.6Strong'), '71 %')}</strong>
                            {t(k('p.alquiler.6After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.alquiler.7Before'),
                                'Con 5.000 € de alquiler, el resto sube al 27,7 % y el techo baja al ',
                            )}
                            <strong>{t(k('p.alquiler.7Strong'), '64,3 %')}</strong>
                            {t(k('p.alquiler.7After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            <strong>
                                {t(
                                    k('p.alquiler.8Strong'),
                                    'Misma carta, mismo equipo, siete puntos menos de margen por el contrato del alquiler.',
                                )}
                            </strong>{' '}
                            <em>{t(k('p.alquiler.8Em'), 'Cifras de ejemplo, no medias del sector.')}</em>
                        </p>
                    </div>

                    <div className={articleUi.stats}>
                        <div className={articleUi.stat}>
                            <p className={articleUi.statLabel}>{t(k('stat.alquilerBajo'), 'Alquiler 3.000 €')}</p>
                            <p className={articleUi.statValueSm}>{t(k('stat.alquilerBajoValue'), 'Techo 71 %')}</p>
                        </div>
                        <div className={articleUi.statAlert}>
                            <p className={articleUi.statLabelAlert}>{t(k('stat.alquilerAlto'), 'Alquiler 5.000 €')}</p>
                            <p className={articleUi.statValueAlert}>{t(k('stat.alquilerAltoValue'), 'Techo 64,3 %')}</p>
                        </div>
                    </div>

                    <h2 id='sin-nomina' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.sinNomina'), 'Si curras cada día sin nómina, tu número es falso')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.sinNomina.1Before'), 'En comidas y bebidas trabajan unos ')}
                            <strong>
                                {t(
                                    k('p.sinNomina.1Strong'),
                                    '148.000 empresarios con asalariados y 119.000 sin asalariados',
                                )}
                            </strong>
                            {t(
                                k('p.sinNomina.1After'),
                                ', según la EPA de 2023 recogida por Fundae. Su trabajo no aparece en ningún gasto de personal.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.sinNomina.2Before'),
                                'La pista: en restauración, las empresas de 0 a 1 empleado generan ',
                            )}
                            <strong>{t(k('p.sinNomina.2Strong'), '3,37 €')}</strong>
                            {t(k('p.sinNomina.2Mid'), ' de valor añadido por euro de personal. Las de 2 a 9, ')}
                            <strong>{t(k('p.sinNomina.2Strong2'), '1,29 €')}</strong>
                            {t(k('p.sinNomina.2After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.sinNomina.3Before'), 'No es que el pequeño sea dos veces y media más eficiente. ')}
                            <em>
                                {t(k('p.sinNomina.3Em'), 'Nuestra lectura: el trabajo del dueño no está en la cuenta.')}
                            </em>
                        </p>
                    </div>

                    <h3 className={articleUi.h3Block}>{t(k('h3.caso'), 'El caso, con números')}</h3>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.caso.1Before'), 'Un restaurante factura ')}
                            <strong>{t(k('p.caso.1Strong'), '30.000 € al mes sin IVA')}</strong>
                            {t(
                                k('p.caso.1After'),
                                '. Producto, 9.000 €. Personal a coste empresa, 9.000 €. Resto de gastos, 6.300 €.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.caso.2Before'), 'Prime cost: ')}
                            <strong>{t(k('p.caso.2Strong'), '60 %')}</strong>
                            {t(k('p.caso.2After'), '. Sobran 5.700 €. La gestoría tiene razón.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.caso.3Before'),
                                'Pero tú estás 60 horas a la semana en la cocina. Pagar a alguien para hacer lo tuyo, con 2.500 € brutos en 14 pagas, costaría unos ',
                            )}
                            <strong>{t(k('p.caso.3Strong'), '3.800 € al mes')}</strong>
                            {t(k('p.caso.3After'), '. ')}
                            <em>
                                {t(
                                    k('p.caso.3Em'),
                                    'Cálculo nuestro: sueldo de jefe de cocina más cotización de empresa de 2026.',
                                )}
                            </em>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.caso.4Before'), 'Con ese coste dentro, el prime cost real es ')}
                            <strong>{t(k('p.caso.4Strong'), '72,7 %')}</strong>
                            {t(k('p.caso.4After'), '. Al otro lado del borde.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(
                                    k('p.caso.5Strong'),
                                    'El negocio genera 1.900 € al mes más allá de tu trabajo, un 6,3 %',
                                )}
                            </strong>
                            {t(k('p.caso.5After'), ', antes de amortizar la cocina.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.caso.6'),
                                'Los otros 3.800 € no se han perdido: son tu sueldo. Pero tienes que saber que lo son.',
                            )}
                        </p>
                    </div>

                    <div className={articleUi.stats}>
                        <div className={articleUi.stat}>
                            <p className={articleUi.statLabel}>{t(k('stat.papel'), 'Prime cost en papel')}</p>
                            <p className={articleUi.statValueSm}>{t(k('stat.papelValue'), '60 %')}</p>
                        </div>
                        <div className={articleUi.statAlert}>
                            <p className={articleUi.statLabelAlert}>{t(k('stat.real'), 'Con tu sueldo dentro')}</p>
                            <p className={articleUi.statValueAlert}>{t(k('stat.realValue'), '72,7 %')}</p>
                        </div>
                    </div>

                    <h2 id='antes-domingo' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.domingo'), 'Tres números para sacar antes del domingo')}
                    </h2>
                    <ol className={articleUi.numList}>
                        {sundayItems.map(item => (
                            <li key={item.lead} className={articleUi.numItem}>
                                <strong>{item.lead}</strong>
                                {item.rest}
                            </li>
                        ))}
                    </ol>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.domingo.cierre'),
                                'Si con tu sueldo dentro pasas del 65 %, el problema ya no es el food cost. Es cuánta gente necesitas por servicio y a qué precio vendes.',
                            )}
                        </p>
                    </div>

                    <h2 id='limites' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.limites'), 'Lo que nadie ha medido todavía')}
                    </h2>
                    <DashList items={limitItems} />
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.limites.cta'),
                                'Si quieres ver ese número con tus facturas y no con un ejemplo, hazlo con Tipi: cuéntanoslo y le ponemos números.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.limites.cierre'),
                                'El 70 % es de otros. Tu línea la pones tú, y con tu sueldo dentro.',
                            )}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <section className={articleUi.cta}>
                    <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>
                        {t(k('h2.cta'), '¿Quieres ver tu prime cost real?')}
                    </h2>
                    <p className={articleUi.ctaSub}>
                        {t(k('cta.sub'), 'Cuéntanoslo y le ponemos números con tus facturas, no con un ejemplo.')}
                    </p>
                    <div className={articleUi.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid)}>
                            {t(k('cta.button'), 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                    <p className={cn(articleUi.ctaClose, 'mt-6')}>
                        {t(k('cta.closeBefore'), 'Desde ')}
                        <TipiLink />
                        {t(k('cta.closeAfter'), ' te ayudamos a poner tu sueldo dentro del número y a fijar tu techo.')}
                    </p>
                </section>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        {SOURCES.map(source => (
                            <li key={source.labelKey}>
                                {source.href ? (
                                    <a
                                        href={source.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={articleUi.link}
                                    >
                                        {t(k(source.labelKey), source.fallback)}
                                    </a>
                                ) : (
                                    t(k(source.labelKey), source.fallback)
                                )}
                            </li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
